import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';

// Status
const PPT_STATUS_ID_TO_LABEL_MAP = {
    unpaid: 'Unpaid',
    delinquent: 'Delinquent',
    installments: 'Installments',
    'turned-over': 'Turned Over',
    processing: 'Processing',
    paid: 'Paid',
};
function getStatusForBill(bill) {
    if (!bill.paid) {
        if (bill.turnedOver)
            return 'turned-over';
        if (bill.hasInstallments)
            return 'installments';
        if (bill.delinquent)
            return 'delinquent';
        return 'unpaid';
    }
    else
        return 'paid';
}
function getStatusLabelForBill(bill) {
    return PPT_STATUS_ID_TO_LABEL_MAP[getStatusForBill(bill)];
}

const pptLookupResponseAdapter = (response) => {
    const result = { accounts: [], parcels: [] };
    for (const parcelResult of response.parcels) {
        const accountData = {
            accountNumber: parcelResult.account_number,
            parcelId: parcelResult.parcel_id + '',
            due: {
                total: parcelResult.amount_due,
                years: parcelResult.bills_with_balance ? parcelResult.bills_with_balance.length : 0,
            },
            payableBillsWithABalance: parcelResult.bills_with_balance.map((value) => value + ''),
            balanceViewBlockedForSomeBills: !!parcelResult.balanceViewBlocks?.length,
            paymentBlockedForSomeBills: !!parcelResult.paymentBlocks?.length,
            unpaidBillViewBlocked: !!parcelResult.unpaidBillViewBlocks?.length,
        };
        Object.keys(accountData)
            .forEach(key => { if (accountData[key] === undefined)
            delete accountData[key]; });
        const parcelData = {
            id: parcelResult.parcel_id + '',
            owner: parcelResult.owner,
            fullAddress: parcelResult.parcel_address,
            appraiserURL: parcelResult.appraserURL,
            parcelMapURL: parcelResult.parcelMapURL,
        };
        Object.keys(parcelData)
            .forEach(key => { if (parcelData[key] === undefined)
            delete parcelData[key]; });
        result.accounts.push(accountData);
        result.parcels.push(parcelData);
    }
    if (response.bills)
        result.bills = response.bills;
    return result;
};
// eslint-disable-next-line complexity
const adaptPPTEntities = (accounts, parcels, bills, sourceContainsAllBills = false) => {
    // Response
    const response = { accounts: [], parcels: [], bills: [] };
    // Accounts
    if (accounts)
        for (const accountInfo of accounts) {
            const accountNumber = accountInfo.accountNumber || accountInfo.account_number;
            const unpaidBillViewBlocked = accountInfo?.unpaidBillViewBlocks && !!accountInfo.unpaidBillViewBlocks.length;
            const account = {
                accountNumber,
                autoDraftType: 'regular',
                unpaidBillViewBlocked,
                payableBillsWithABalance: accountInfo.payableBillsWithABalance || accountInfo.billsWithABalance || accountInfo.bills_with_balance,
                parcelId: accountInfo.parcelId || accountInfo.parcel_id,
                due: accountInfo.due
            };
            if (sourceContainsAllBills) {
                account.balanceViewBlockedForSomeBills = false;
                account.paymentBlockedForSomeBills = false;
                account.bills = [];
                account.due = { years: 0, total: 0 };
            }
            if (bills)
                for (const billInfo of bills) {
                    const balanceViewBlocked = !!billInfo.balanceViewBlocks?.length;
                    const paymentBlocked = balanceViewBlocked || !!billInfo.paymentBlocks?.length;
                    if (billInfo.id === response.latestYear && billInfo.hasInstallments)
                        account.autoDraftType = 'installments';
                    if (sourceContainsAllBills) {
                        if (balanceViewBlocked)
                            account.balanceViewBlockedForSomeBills = true;
                        if (paymentBlocked)
                            account.paymentBlockedForSomeBills = true;
                        if (billInfo.due > 0) {
                            account.due.years++;
                            account.due.total += billInfo.due;
                        }
                        account.bills.push(billInfo.id);
                    }
                }
            response.accounts.push(account);
        }
    // Parcels
    if (parcels)
        for (const parcelInfo of parcels) {
            const parcel = {};
            parcel.id = parcelInfo.parcel_id;
            parcel.owner = parcelInfo.owner;
            parcel.address = {
                address1: parcelInfo.parcel_address_1,
                address2: parcelInfo.parcel_address_2,
                state: parcelInfo.parcel_state,
                city: parcelInfo.parcel_city,
                zip: parcelInfo.parcel_zip,
            };
            if (parcelInfo.parcel_details)
                parcel.details = {
                    propertyType: parcelInfo.parcel_details.property_type,
                    situs: parcelInfo.parcel_details.situs,
                    legal: parcelInfo.parcel_details.parcel_legal,
                    exemptionCodes: parcelInfo.parcel_details.exemption_codes,
                    escrowCode: parcelInfo.parcel_details.escrow_code,
                    landUseCode: parcelInfo.parcel_details.parcel_land_use_code,
                };
            if (parcelInfo.parcel_coordinates)
                parcel.coordinates = {
                    latitude: parcelInfo.parcel_coordinates.latitude,
                    longitude: parcelInfo.parcel_coordinates.longitude
                };
            if (bills)
                for (const billInfo of bills) {
                    if ((billInfo.parcelId || billInfo.parcel_id) === parcel.id) {
                        if (billInfo.owner && !parcel.owner)
                            parcel.owner = billInfo.owner; // This is correct, not temporary
                        if (billInfo.appraserURL && !parcel.appraiserURL)
                            parcel.appraiserURL = billInfo.appraserURL;
                        if (billInfo.parcelMapURL && !parcel.parcelMapURL)
                            parcel.parcelMapURL = billInfo.parcelMapURL;
                    }
                }
            response.parcels.push(parcel);
        }
    response.latestYear = +bills
        ?.map((bill) => bill.taxYear)
        .reduce((reduced, current) => current > reduced ? current : reduced, 0);
    // Bills
    if (bills)
        for (const billInfo of bills) {
            const billAccountNumber = billInfo.account_number || billInfo.accountNumber;
            const balanceViewBlocked = !!billInfo.balanceViewBlocks?.length;
            const paymentBlocked = balanceViewBlocked || !!billInfo.paymentBlocks?.length;
            delete billInfo.balanceViewBlocks;
            delete billInfo.paymentBlocks;
            const bill = JSON.parse(JSON.stringify(billInfo));
            delete bill.certificate;
            delete bill.account_number;
            delete bill.bill_number;
            delete bill.parcel_id;
            if (billAccountNumber)
                bill.accountNumber = billAccountNumber;
            if (billInfo.bill_number)
                bill.billNumber = billInfo.bill_number;
            if (billInfo.parcel_id)
                bill.parcelId = billInfo.parcel_id;
            bill.taxYear = +(bill.taxYear || bill.tax_year);
            if (bill.payments) {
                bill.amountPaid = 0;
                for (const payment of bill.payments)
                    if (payment.amount)
                        bill.amountPaid += payment.amount;
            }
            bill.balanceViewBlocked = balanceViewBlocked;
            bill.paymentBlocked = paymentBlocked;
            // Sort payments from newer to older.
            // With null dates considered to be older payments.
            if (bill.payments !== undefined)
                bill.payments = bill.payments?.sort((a, b) => a.date <= b.date ? 1 : -1);
            response.bills.push(bill);
        }
    for (const parcel of response.parcels)
        Object.keys(parcel).forEach(key => { if (parcel[key] === undefined)
            delete parcel[key]; });
    for (const account of response.accounts)
        Object.keys(account).forEach(key => { if (account[key] === undefined)
            delete account[key]; });
    for (const bill of response.bills)
        Object.keys(bill).forEach(key => { if (bill[key] === undefined)
            delete bill[key]; });
    return response;
};
const adaptPPTBills = (source, containsAllBills = false) => {
    return adaptPPTEntities(source.due.PPT.accounts, source.due.PPT.parcels, source.due.PPT.bills, containsAllBills);
};

/* eslint-disable */
class PPTFakeEntities {
    constructor() {
        this.createdBills = {};
        this.createdParcels = {};
        this.createdAccounts = {};
        this.billsInCart = [];
        this.partialAmounts = new Map();
    }
    createAccountSuite(accountNumber, currentTaxYear, query) {
        const turnedOverType = Math.random() < .9
            ? Math.random() < .5
                ? 'partial'
                : 'full'
            : false;
        const account = this.createAccount(accountNumber);
        accountNumber = account.accountNumber;
        let parcel = this.createParcel(account.parcelId, query);
        parcel = { ...parcel };
        account.parcelId = parcel.id;
        let history = this.createAccountHistory(accountNumber, parcel.id, Math.random() < .5, turnedOverType);
        history.sort((a, b) => b.taxYear - a.taxYear);
        account.bills = history.map(bill => bill.id);
        account.payableBillsWithABalance = history
            .filter(bill => bill.due && !bill.balanceViewBlocked && !bill.paymentBlocked)
            .map(bill => bill.id);
        account.balanceViewBlockedForSomeBills = !!history.filter(bill => bill.balanceViewBlocked).length;
        account.paymentBlockedForSomeBills = !!history.filter(bill => bill.paymentBlocked).length;
        account.due =
            {
                total: history.reduce((reduced, bill) => reduced + bill.due, 0),
                years: history.reduce((reduced, bill) => reduced + bill.due ? 1 : 0, 0),
            };
        const latestYear = history.map((bill) => bill.taxYear).reduce((reduced, current) => current > reduced ? current : reduced, 0);
        if (currentTaxYear === undefined)
            currentTaxYear = latestYear;
        let bill = history.find(bill => bill.taxYear == latestYear);
        if (bill) {
            bill = { ...bill };
            history = history.map(b => b.taxYear != currentTaxYear ? b : bill);
        }
        return { account, parcel, bills: history, latestYear };
    }
    createAccount(accountNumber) {
        const defaultAccount = {
            accountNumber: accountNumber || this.createAccountNumber(),
            autoDraftType: Math.random() < .5 ? 'regular' : 'installments',
            due: Math.random() < .3
                ? { years: 0, total: 0 }
                : {
                    years: Math.round(7 * Math.random()),
                    total: Math.round(100000 * Math.random()) / 100,
                },
        };
        const existingAccount = accountNumber && this.createdAccounts[accountNumber];
        const finalAccount = existingAccount ? { ...defaultAccount, ...existingAccount } : defaultAccount;
        this.createdAccounts[finalAccount.accountNumber] = finalAccount;
        return finalAccount;
    }
    createParcel(parcelId, query) {
        const existingParcel = parcelId && this.createdParcels[parcelId];
        if (parcelId === undefined)
            parcelId = Math.round(Math.random() * 9999999999) + '';
        const defaultParcel = {
            id: parcelId,
            owner: this.createParcelOwner(query && query.toUpperCase()),
            dba: 'DBA',
            address: {
                address1: this.createAddress1().toUpperCase(),
                state: 'FL',
                city: Math.random() < .9 ? this.getRandomCity().toUpperCase() : undefined,
                zip: Math.random() < .8 ? Math.round(Math.random() * 99999) : undefined,
            },
            details: this.createParcelDetails(),
            coordinates: {
                latitude: '28.53202146183871',
                longitude: '-81.39052470888515',
            },
            appraiserURL: 'https://www.google.com/',
            parcelMapURL: 'https://www.google.com/',
        };
        const finalParcel = existingParcel ? { ...defaultParcel, ...existingParcel } : defaultParcel;
        this.createdParcels[finalParcel.id] = finalParcel;
        return finalParcel;
    }
    createAddress1() {
        const streetOptions = [
            'Augusta Ave',
            'Meadow Drive',
            'Ridge Ave',
            'Grand St',
            'Park St',
            'Selby Dr',
            'Chestnut Ave',
            'Courtland St',
            'Nicolls St',
            'Creekside Drive',
            'North Bridle Lane',
            'South Birch Hill Street',
            'Hamilton Rd',
            'Beach St',
            'Miller St',
            'Pacific Lane',
            'Greenrose St',
            'Golf St',
            'North Meadow St',
            'Division Drive',
            'Third St',
            'Henry Street',
            'Cedarwood Ave',
            'Rockledge Lane',
            'Studebaker Street',
            'Vine St',
            'Fremont St',
            'Bishop Ave',
            'Oak Dr',
            'Buckingham Dr',
            'Durham Ave',
            'Sleepy Hollow Ave',
            'Proctor St',
            'South Saxton Lane',
            'West Alderwood Lane',
            'Ketch Harbour St',
            'Park Ave',
            'Tanglewood St',
            'Deerfield Lane',
            'Beaver Ridge Road',
            'Linden Ave',
            'Sherwood Street',
            'Shirley Drive',
            'Walnutwood Circle',
            'Fairway Rd',
            'East St',
            'Yukon Drive',
        ];
        return `${1 + Math.round(Math.random() * 3000)} ${streetOptions[Math.floor(Math.random() * streetOptions.length)]}`;
    }
    getRandomCity() {
        const cityOptions = [
            'Fort Lauderdale',
            'Orlando',
            'Dunedin',
            'Pensacola',
            'New Port Richey',
            'Boynton Beach',
            'Sanford',
            'Deltona',
            'Pompano Beach',
            'Winter Springs',
            'Hollywood',
            'Clermont',
            'Daytona Beach',
            'Apopka',
            'Hialeah',
            'Port Richey',
            'Orange Park',
            'Kissimmee',
            'Jacksonville',
            'Key West',
            'Stuart',
            'Miami',
            'Melbourne',
        ];
        return cityOptions[Math.floor(Math.random() * cityOptions.length)];
    }
    createParcelOwner(uppercasedQuery) {
        if (uppercasedQuery) {
            switch (Math.floor(Math.random() * 10)) {
                case 0: return 'RICHARD ' + uppercasedQuery + '';
                case 1: return 'EMMANUELLE ' + uppercasedQuery;
                case 2: return 'RICHARD ' + uppercasedQuery + ' TRUST AGREEMENT';
                case 3: return 'METHODIST CHURCH OF AMERICA + ' + uppercasedQuery + ' AMADOR';
                case 4: return 'JACK ' + uppercasedQuery;
                case 5: return 'ANNE MARIE ' + uppercasedQuery;
                case 6: return uppercasedQuery;
                case 7: return 'JONATHAN GINZBURG + ' + uppercasedQuery;
                case 8: return uppercasedQuery + ' LINDA';
                case 9:
                default: return 'DANIEL ' + uppercasedQuery + ' TRUST AGREEMENT';
            }
        }
        else {
            const ownerOptions = [
                'ABLES GHISLAINE',
                'AUGUSTIN MAGDA JULIANA',
                'VARGAS THEODORE + MITSY',
                'BAY STREAM LLC',
                'TURNER MEMORIAL',
                'MICHAEL YVROSE ET AL',
                'EBAAR/JET',
                'CHILLEMI JOHN C TRS',
                'WIBORG RUBY TRUSTEE OF',
                'HAYS RALPH L',
                'SISULAK CARMEN S',
                'PAVIA WILLIAM + ELAINE',
                'THERING SELBY J TRUST',
                'PROUD SANTIAGO FRANCISCO L',
                'SOSA RACHEL G ET AL +',
                'OSBORNE MARIE J',
                'CAGLE FAINA',
                'GORBACH ANNA MARIE',
                'FEENEY PLACID DEVELOPMENT CORP INC',
                'LAKE PROPERTIES OF BUCKHORN LLC',
                'PRIME STEPHEN C',
                'WEEKS DONALD B TRUSTEE',
                'SOLDINI ELSIE P',
                'HOWE MANVILLA ANN',
                'EVANS ELMER D + RHODA',
                'SHOCKLEY NANCY',
                'RICARDO BETTY J',
                'SNYDER PINAN MARGARET',
                'SALAZAR CORP INC',
                'FEO RICKIE W',
                'NORTHCUTT ANDY P',
                'JACOME LILLIAN',
                'KOSIER WALTER M',
                'MIRASKY JODY',
                'GREGORY WILLIAM P',
                'HOLTON RASHID',
                'KHAN THOMAS',
                'STEINGRABER GRACE',
                'BUTCHER WILLIAM J + MARTHA',
                'BOHLER TIMOTHY J + APRIL M',
                'SULLIVAN MILTON F + GLENDA',
            ];
            return ownerOptions[Math.floor(Math.random() * ownerOptions.length)];
        }
    }
    createParcelDetails() {
        return ({
            propertyType: 'Tangible',
            situs: '3214 HIGHLANDS HAMIC AVON PARK,',
            legal: 'AVON PARK LAKES UNIT 2\nPB 4-PG 89\nLOTS 507 + 508\n',
            exemptionCodes: ['HX', 'HB', 'WB'],
            escrowCode: ['CL'],
            landUseCode: '0100',
        });
    }
    createBillCharges() {
        return ({
            adValorem: {
                charges: [
                    {
                        description: 'Hendry County',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                    {
                        description: 'Hendry Regional Medical Center',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                    {
                        description: 'Hendry Med Center - Debt Service',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                    {
                        description: 'Schools General Fund',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                    {
                        description: 'Schools Capital Outlay',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                    {
                        description: 'Schools Discretionary',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                    {
                        description: 'South Florida Water District',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                    {
                        description: 'South Florida Water Basin',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                    {
                        description: 'South Florida Water Everglades',
                        phone: 8634864829,
                        assessed: '0.00000',
                        exempt: '0.00000',
                        taxable: '0.00000',
                        millage: '8.49090',
                        amount: 0,
                    },
                ],
                total: 0,
            },
            nonAdValorem: {
                charges: [
                    {
                        description: '75DU - Mosquito Control District',
                        amount: 30,
                    },
                    {
                        description: '75DU - Mosquito Control District',
                        amount: 10,
                    },
                    {
                        description: '75DU - Mosquito Control District',
                        amount: 50,
                    },
                    {
                        description: '75DU - Mosquito Control District',
                        amount: 100,
                    },
                    {
                        description: '75DU - Mosquito Control District',
                        amount: 235,
                    },
                ],
                total: 235,
            },
            feesAndInterests: {
                charges: [
                    {
                        description: 'Resolution D574-M',
                        amount: 45,
                    }
                ],
                total: 45,
            }
        });
    }
    createAccountNumber() {
        return 'T000000-0000' + Math.round(9999999 * Math.random());
    }
    createAccountHistory(accountNumber, parcelId, allowDue = true, turnedOverType = false) {
        const history = [];
        if (accountNumber === undefined)
            accountNumber = this.createAccountNumber();
        if (parcelId === undefined)
            parcelId = Math.round(Math.random() * 999999999999) + '';
        for (let i = 0; i < 10; i++) {
            const bill = this.createBill(accountNumber, parcelId, i, allowDue, turnedOverType);
            history.push(bill);
        }
        return history;
    }
    createBill(accountNumber, parcelId, i, allowDue = true, turnedOverType) {
        const doInstallments = true;
        const parcel = this.createParcel(parcelId);
        const owner = parcel.owner;
        const amount = Math.round(20000 * Math.random()) / 100;
        const paid = allowDue ? i < 5 : true;
        const delinquent = !paid && i != 9;
        const hasInstallments = doInstallments && i == 5;
        const due = paid ? 0 : (hasInstallments ? amount / 3 * 2 : amount);
        const payor = i < 2 ? 'MIRTHA BUSNELLI' : owner;
        const balanceViewBlocked = !paid && Math.random() < 1 / 15;
        const paymentBlocked = !paid && balanceViewBlocked || Math.random() < 1 / 15;
        const turnedOver = !paid && turnedOverType && (turnedOverType === 'partial'
            ? i < 7
            : true);
        let payments;
        if (paid) {
            payments = [this.createPayment(amount, payor)];
        }
        else if (hasInstallments) {
            payments = [];
            for (let j = 0; j < 3; j++)
                payments.push(this.createPayment(amount / 4, payor, j == 2));
        }
        const billId = `${accountNumber}00${i}`;
        const defaultBill = {
            id: billId,
            billNumber: 10000000 + Math.round(Math.random() * 9999999999) + '',
            accountNumber,
            taxYear: 2010 + i,
            parcelId,
            owner,
            dba: 'dba',
            paid,
            due,
            amountPaid: amount - due,
            delinquent,
            hasInstallments,
            payments,
            balanceViewBlocked,
            paymentBlocked,
            charges: this.createBillCharges(),
            projections: [
                { date: '2018-12-31', amount: 450 },
                { date: '2019-1-31', amount: 473.30 },
                { date: '2019-2-28', amount: 497.81 },
                { date: '2019-3-31', amount: 523.58 },
                { date: '2019-4-30', amount: 550.69 },
                { date: '2019-5-31', amount: 579.21 },
            ],
            certificate: {
                holder: {
                    name: 'D3 INVESTMENT AND CONSULTATION',
                    address1: '1148 WOODMINSTER DR',
                    city: 'SAN JOSE',
                    state: 'CA',
                    zip: 95121,
                    contact: Math.random() < .5 ? 'relay' : { email: 'd3investment@gmail.com' }
                },
                info: {
                    year: 2014,
                    number: 320,
                    sold: '2014-06-1',
                    bidPercentage: 18,
                    face: 127.5,
                    interest: 38.15,
                    tcFee: 6.25,
                    total: 171.55,
                },
            },
            printUrl: 'https://www.google.com/',
            historyPrintUrl: `https://highlands-fl.mygovonline.com/next/mod.php?mod=propertytax&mode=public_history_print&bill_id=${parcelId}00${i}&pr=ol`,
            cityTaxSite: null,
            countyTaxSite: null,
            turnedOver,
            delegateLink: turnedOver ? 'https://www.google.com/' + billId : null,
        };
        const existingBill = this.createdBills[billId];
        const finalBill = existingBill ? { ...defaultBill, ...existingBill } : defaultBill;
        this.createdBills[finalBill.id] = finalBill;
        return finalBill;
    }
    createPayment(amount, payor, prePayment = false) {
        return ({
            receipt: 1000000000 + Math.floor(Math.random() * 999999999) + '',
            status: 'Complete',
            amount,
            payor,
            date: (2017 + Math.round(Math.random() * 2)) + '-' + (1 + Math.round(Math.random() * 11)) + '-' + (1 + Math.round(Math.random() * 27)),
            printUrl: 'https://www.google.com/',
            isPrePayment: prePayment
        });
    }
    turnBillIntoCartItem(bill) {
        const itemId = 'ppt-' + bill.id;
        return {
            type: 'ppt',
            id: itemId,
            amount: bill.due,
            partialAmount: this.partialAmounts?.get(bill.id),
            billId: bill.id,
            accountNumber: bill.accountNumber,
            taxYear: bill.taxYear,
        };
    }
    newCartItemsAndExisting(newItems) {
        const allItemsWithDuplicates = this.billsInCart
            .map(id => this.createdBills[id])
            .filter(bill => !!bill)
            .map(bill => this.turnBillIntoCartItem(bill))
            .concat(newItems);
        const allItemsUniqueIds = new Set(allItemsWithDuplicates.map(item => item.id));
        return Array.from(allItemsUniqueIds).map(id => allItemsWithDuplicates.find(item => item.id === id));
    }
    getGrandTotalWithoutFeesForBills(bills) {
        return bills.reduce((acc, curr) => acc + (curr.partialAmount || curr.amount), 0);
    }
}
PPTFakeEntities.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: PPTFakeEntities, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PPTFakeEntities.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: PPTFakeEntities, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: PPTFakeEntities, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PPTFakeEntities, PPT_STATUS_ID_TO_LABEL_MAP, adaptPPTBills, adaptPPTEntities, getStatusForBill, getStatusLabelForBill, pptLookupResponseAdapter };
